import { useEffect, useMemo, useState } from 'react';
import { useSesi } from '../state';
import { segarkanKatalog, nomorBerikut } from '../lib/katalog';
import { antrekan, kirimSemua, daftarDitolak, buang, type ItemAntrean } from '../lib/outbox';
import { TagihanTerbuka } from './TagihanTerbuka';
import {
  hitungTotal, rupiah, nomorStruk,
  type Katalog, type MenuItem, type OrderInput, type PaymentInput,
} from '@santap/shared';
import { api } from '../lib/api';
import { Bayar } from './Bayar';

export interface BarisKeranjang {
  kunci: string;
  id: string;
  itemId: string;
  nama: string;
  hargaSatuan: number;
  hargaModifier: number;
  qty: number;
  stasiun: string | null;
  catatan: string | null;
  modifier: { modifierId: string; nama: string; hargaTambah: number }[];
}

export function Kasir() {
  const { sesi, antrean, simpanSesi } = useSesi();
  const [katalog, setKatalog] = useState<Katalog | null>(null);
  const [kategoriAktif, setKategoriAktif] = useState<string | null>(null);
  const [baris, setBaris] = useState<BarisKeranjang[]>([]);
  const [tipe, setTipe] = useState<'dine_in' | 'take_away'>('dine_in');
  const [urut, setUrut] = useState(1);
  const [pilihMod, setPilihMod] = useState<MenuItem | null>(null);
  const [bayarBuka, setBayarBuka] = useState(false);
  const [pesan, setPesan] = useState<string | null>(null);
  const [tolakBuka, setTolakBuka] = useState(false);
  const [ditolak, setDitolak] = useState<ItemAntrean[]>([]);
  const [tagihanBuka, setTagihanBuka] = useState(false);
  const [jmlTagihan, setJmlTagihan] = useState(0);

  // --- muat katalog: lokal dulu, segarkan di latar
  useEffect(() => {
    if (!sesi) return;
    (async () => {
      const h = await segarkanKatalog(sesi.outletId);
      if (h.katalog) {
        setKatalog(h.katalog);
        setKategoriAktif((k) => k ?? h.katalog!.kategori[0]?.id ?? null);
      }
      if (h.pesan) tampil(h.pesan);
      setUrut(await nomorBerikut(sesi.terminalId));
    })();
  }, [sesi?.outletId]);

  useEffect(() => {
    if (antrean.ditolak > 0) void daftarDitolak().then(setDitolak);
  }, [antrean.ditolak]);

  // Pelanggan kiosk datang membawa nomor antrean, meja memanggil untuk
  // membayar. Kasir perlu tahu ada yang menunggu TANPA harus membuka layar
  // dan memeriksa — jadi jumlahnya ditampilkan terus di bilah atas.
  useEffect(() => {
    if (!sesi || !antrean.online) return;
    let hidup = true;
    const hitung = async () => {
      try {
        const d = await api.tagihanTerbuka(sesi.outletId);
        if (hidup) setJmlTagihan(d.length);
      } catch { /* jaringan tersendat; coba lagi nanti */ }
    };
    void hitung();
    const t = window.setInterval(hitung, 12_000);
    return () => { hidup = false; window.clearInterval(t); };
  }, [sesi?.outletId, antrean.online, tagihanBuka]);

  function tampil(t: string) {
    setPesan(t);
    window.setTimeout(() => setPesan((p) => (p === t ? null : p)), 2800);
  }

  const struk = useMemo(
    () => nomorStruk(sesi?.terminalKode ?? 'K1', urut),
    [sesi?.terminalKode, urut],
  );

  const total = useMemo(() => {
    if (!katalog) return null;
    return hitungTotal(
      baris.map((b) => ({
        hargaSatuan: b.hargaSatuan, qty: b.qty, hargaModifier: b.hargaModifier,
        diskonBaris: 0, kenaPajak: true,
      })),
      katalog.aturan,
      0,
    );
  }, [baris, katalog]);

  if (!sesi || !katalog || !total) {
    return <div className="tengah"><div className="putar" /></div>;
  }

  const itemTampil = katalog.item.filter((i) => i.categoryId === kategoriAktif);

  function tambah(item: MenuItem, mods: BarisKeranjang['modifier']) {
    const hargaMod = mods.reduce((a, m) => a + m.hargaTambah, 0);
    const kunci = item.id + '|' + mods.map((m) => m.nama).sort().join(',');
    setBaris((lama) => {
      const ada = lama.find((b) => b.kunci === kunci);
      if (ada) return lama.map((b) => (b.kunci === kunci ? { ...b, qty: b.qty + 1 } : b));
      return [...lama, {
        kunci, id: crypto.randomUUID(), itemId: item.id, nama: item.nama,
        hargaSatuan: item.harga, hargaModifier: hargaMod, qty: 1,
        stasiun: item.stasiunDapur, catatan: null, modifier: mods,
      }];
    });
  }

  function ubahQty(kunci: string, delta: number) {
    setBaris((lama) =>
      lama.flatMap((b) => {
        if (b.kunci !== kunci) return [b];
        const q = b.qty + delta;
        return q <= 0 ? [] : [{ ...b, qty: q }];
      }),
    );
  }

  function klikItem(item: MenuItem) {
    if (item.grupModifier.length) setPilihMod(item);
    else tambah(item, []);
  }

  /** Susun pesanan dari keranjang. Dipakai jalur offline maupun daring. */
  function susunPesanan(): OrderInput {
    return {
      id: crypto.randomUUID(),
      outletId: sesi!.outletId,
      terminalId: sesi!.terminalId,
      shiftId: sesi!.shiftId ?? null,
      tableId: null,
      nomorStruk: struk,
      channel: 'kasir',
      tipe,
      namaPelanggan: null, teleponPelanggan: null, jumlahTamu: null, catatan: null,
      diskon: 0,
      dibuatPada: new Date().toISOString(),
      dibuatOffline: !navigator.onLine,
      katalogVersi: katalog!.versi,
      totalKlien: total!.total,
      baris: baris.map((b, i) => ({
        id: b.id, itemId: b.itemId, namaItem: b.nama,
        hargaSatuan: b.hargaSatuan, qty: b.qty, hargaModifier: b.hargaModifier,
        diskonBaris: 0, kenaPajak: true, stasiunDapur: b.stasiun,
        catatan: b.catatan, urutan: i, modifier: b.modifier,
      })),
    };
  }

  async function bersihkan(catatan: string) {
    setBaris([]);
    setBayarBuka(false);
    setUrut(await nomorBerikut(sesi!.terminalId));
    tampil(catatan);
  }

  /**
   * Selesaikan pesanan tunai atau EDC.
   *
   * Pesanan dan pembayaran masuk antrean lokal LEBIH DULU, baru dicoba dikirim.
   * Kasir tidak pernah menunggu server — kalau jaringan mati, struk tetap
   * tercetak dan pelanggan tetap dilayani.
   */
  async function selesaikan(pembayaran: Omit<PaymentInput, 'orderId'>) {
    const pesanan = susunPesanan();
    await antrekan('pesanan', pesanan);
    await antrekan('pembayaran', { ...pembayaran, orderId: pesanan.id } as PaymentInput);
    await bersihkan(
      navigator.onLine
        ? 'Struk tercetak · tiket dapur terkirim'
        : 'Tersimpan di perangkat · terkirim otomatis saat online',
    );
  }

  /**
   * Siapkan pesanan untuk QRIS.
   *
   * QRIS satu-satunya tender yang TIDAK bisa lewat antrean offline: gateway
   * perlu nominal yang mengacu pada pesanan yang sudah ada di server sebelum
   * QR bisa dibuat. Jadi di sini pesanan dikirim langsung dan ditunggu —
   * satu-satunya tempat di alur kasir yang menunggu jaringan, dan itu wajar
   * karena tanpa jaringan QRIS memang mustahil.
   */
  async function siapkanPesananDaring(): Promise<string> {
    const pesanan = susunPesanan();
    await api.kirimPesanan(pesanan);
    return pesanan.id;
  }

  /** Dipanggil setelah gateway memastikan QRIS lunas. */
  async function selesaiQris() {
    await bersihkan('Pembayaran QRIS diterima · tiket dapur terkirim');
  }

  const peringatanAntrean =
    antrean.menunggu >= 100 || antrean.tertuaMenit >= 240;

  return (
    <div className="app">
      <div className="top">
        <div className="logo"><i />Santap</div>
        <div className="meta">
          <div>Outlet <b>{sesi.outletNama}</b></div>
          <div>Terminal <b>{sesi.terminalKode}</b></div>
          <div>Kasir <b>{sesi.pengguna.nama}</b></div>
        </div>
        <div className="kanan">
          {jmlTagihan > 0 && (
            <button className="chip tagihan" onClick={() => setTagihanBuka(true)}>
              💵 {jmlTagihan} tagihan menunggu
            </button>
          )}
          {antrean.ditolak > 0 && (
            <button className="chip tolak" onClick={() => setTolakBuka(true)}>
              ⚠ {antrean.ditolak} perlu perhatian
            </button>
          )}
          {antrean.menunggu > 0 && (
            <span className="chip antre">⇅ {antrean.menunggu} antre</span>
          )}
          <span className={`chip ${antrean.online ? '' : 'off'}`}>
            <i />{antrean.online ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>

      {!antrean.online && (
        <div className="pita warn">
          <span>⚠︎</span>
          <span>
            Mode offline — kasir tetap jalan penuh. Tunai dan EDC normal; QRIS butuh internet.
          </span>
        </div>
      )}

      {peringatanAntrean && (
        <div className="pita crit">
          <span>⚠︎</span>
          <span>
            {antrean.menunggu} pesanan menunggu terkirim, tertua {antrean.tertuaMenit} menit.
            Segera sambungkan internet agar data tidak menumpuk terlalu lama.
          </span>
          <button onClick={() => void kirimSemua()}>Coba kirim sekarang</button>
        </div>
      )}

      <div className="utama">
        <div className="kiri">
          <div className="kat">
            {katalog.kategori.map((k) => (
              <button key={k.id} aria-pressed={k.id === kategoriAktif}
                      onClick={() => setKategoriAktif(k.id)}>
                {k.nama}
              </button>
            ))}
          </div>
          <div className="kisi">
            {itemTampil.map((i) => (
              <button key={i.id} className="item" disabled={!i.tersedia}
                      onClick={() => klikItem(i)}>
                <div className="nm">{i.nama}</div>
                <div>
                  <div className="hg">{i.tersedia ? rupiah(i.harga) : 'Habis'}</div>
                  {i.grupModifier.length > 0 && <div className="tag">pilihan</div>}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="kanan-panel">
          <div className="kh">
            <div className="no">Struk <b>{struk}</b></div>
            <div className="seg">
              <button aria-pressed={tipe === 'dine_in'} onClick={() => setTipe('dine_in')}>
                Makan di sini
              </button>
              <button aria-pressed={tipe === 'take_away'} onClick={() => setTipe('take_away')}>
                Bawa pulang
              </button>
            </div>
          </div>

          <div className="baris-list">
            {baris.length === 0 ? (
              <div className="kosong">
                <b>Belum ada pesanan</b>
                Ketuk menu di sebelah kiri untuk menambahkan.
              </div>
            ) : baris.map((b) => (
              <div className="br" key={b.kunci}>
                <div className="qty">
                  <button onClick={() => ubahQty(b.kunci, -1)} aria-label="Kurangi">−</button>
                  <span>{b.qty}</span>
                  <button onClick={() => ubahQty(b.kunci, 1)} aria-label="Tambah">+</button>
                </div>
                <div className="isi">
                  <div className="nm">{b.nama}</div>
                  {b.modifier.length > 0 && (
                    <div className="mod">{b.modifier.map((m) => m.nama).join(' · ')}</div>
                  )}
                  <div className="sat">
                    {rupiah(b.hargaSatuan + b.hargaModifier)} × {b.qty}
                  </div>
                </div>
                <div className="jml">{rupiah((b.hargaSatuan + b.hargaModifier) * b.qty)}</div>
              </div>
            ))}
          </div>

          <div className="total">
            <div className="tr"><span>Subtotal</span><span>{rupiah(total.subtotal)}</span></div>
            {katalog.aturan.servicePersen > 0 && (
              <div className="tr">
                <span>Service charge {katalog.aturan.servicePersen}%</span>
                <span>{rupiah(total.serviceCharge)}</span>
              </div>
            )}
            <div className="tr">
              <span>PB1 {katalog.aturan.pb1Persen}%</span><span>{rupiah(total.pb1)}</span>
            </div>
            {total.pembulatan !== 0 && (
              <div className="tr"><span>Pembulatan</span><span>{rupiah(total.pembulatan)}</span></div>
            )}
            <div className="tr besar"><span>Total</span><span>{rupiah(total.total)}</span></div>
          </div>

          <div className="aksi">
            <button className="tbl samar" disabled={!baris.length} onClick={() => setBaris([])}>
              Batal
            </button>
            <button className="tbl utama" disabled={!baris.length} onClick={() => setBayarBuka(true)}>
              {baris.length ? `Bayar · ${rupiah(total.total)}` : 'Bayar'}
            </button>
          </div>
        </div>
      </div>

      {pilihMod && (
        <PilihModifier item={pilihMod} katalog={katalog}
                       tutup={() => setPilihMod(null)}
                       pilih={(mods) => { tambah(pilihMod, mods); setPilihMod(null); }} />
      )}

      {bayarBuka && (
        <Bayar total={total.total} struk={struk} online={antrean.online}
               tutup={() => setBayarBuka(false)} selesai={selesaikan}
               siapkanPesananDaring={siapkanPesananDaring} selesaiQris={selesaiQris} />
      )}

      {tagihanBuka && (
        <TagihanTerbuka outletId={sesi.outletId}
                        tutup={() => setTagihanBuka(false)}
                        lunas={(pesan) => { setTagihanBuka(false); tampil(pesan); }} />
      )}

      {tolakBuka && (
        <DaftarDitolak daftar={ditolak} tutup={() => setTolakBuka(false)}
                       buangSatu={async (id) => {
                         await buang(id);
                         setDitolak(await daftarDitolak());
                       }} />
      )}

      {pesan && <div className="toast">{pesan}</div>}
    </div>
  );
}

// ------------------------------------------------------------ modifier
function PilihModifier({
  item, katalog, tutup, pilih,
}: {
  item: MenuItem; katalog: Katalog; tutup: () => void;
  pilih: (m: BarisKeranjang['modifier']) => void;
}) {
  const grup = katalog.grupModifier.filter((g) => item.grupModifier.includes(g.id));
  const [dipilih, setDipilih] = useState<Record<string, string[]>>(
    Object.fromEntries(grup.map((g) => [g.id, []])),
  );

  const semuaOpsi = grup.flatMap((g) => g.opsi);
  const terpilih = Object.values(dipilih).flat()
    .map((id) => semuaOpsi.find((o) => o.id === id)!)
    .filter(Boolean);
  const extra = terpilih.reduce((a, o) => a + o.hargaTambah, 0);
  const siap = grup.every((g) => (dipilih[g.id]?.length ?? 0) >= g.minPilih);

  function toggle(grupId: string, opsiId: string, maks: number) {
    setDipilih((lama) => {
      const cur = lama[grupId] ?? [];
      const at = cur.indexOf(opsiId);
      let baru: string[];
      if (at >= 0) baru = cur.filter((x) => x !== opsiId);
      else baru = cur.length >= maks ? [...cur.slice(1), opsiId] : [...cur, opsiId];
      return { ...lama, [grupId]: baru };
    });
  }

  return (
    <div className="tirai" onClick={(e) => e.target === e.currentTarget && tutup()}>
      <div className="modal">
        <div className="mh">
          <div>
            <h2>{item.nama}</h2>
            <div className="sub">{rupiah(item.harga)}</div>
          </div>
          <button className="x" onClick={tutup} aria-label="Tutup">×</button>
        </div>
        <div className="mb">
          {grup.map((g) => (
            <div key={g.id} style={{ marginBottom: 17 }}>
              <p style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '.06em',
                textTransform: 'uppercase', color: 'var(--muted)', margin: '0 0 8px',
              }}>
                {g.nama}{g.wajib ? ' · wajib' : ` · maks ${g.maxPilih}`}
              </p>
              <div style={{
                display: 'grid', gap: 6,
                gridTemplateColumns: 'repeat(auto-fit,minmax(112px,1fr))',
              }}>
                {g.opsi.map((o) => {
                  const aktif = dipilih[g.id]?.includes(o.id) ?? false;
                  return (
                    <button key={o.id} onClick={() => toggle(g.id, o.id, g.maxPilih)}
                            style={{
                              padding: '10px 11px', borderRadius: 8, textAlign: 'left',
                              fontSize: 12.5, fontWeight: 600, lineHeight: 1.3,
                              border: `1px solid ${aktif ? 'var(--primary)' : 'var(--line)'}`,
                              background: aktif ? 'var(--primary-soft)' : 'var(--panel-2)',
                            }}>
                      {o.nama}
                      {o.hargaTambah > 0 && (
                        <span style={{
                          display: 'block', fontWeight: 500, color: 'var(--muted)',
                          fontSize: 11, marginTop: 2, fontFamily: '"IBM Plex Mono",monospace',
                        }}>
                          +{rupiah(o.hargaTambah)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="mf">
          <div style={{
            fontSize: 16, fontWeight: 800, alignSelf: 'center',
            fontFamily: '"IBM Plex Mono",monospace', flex: '0 0 auto', paddingRight: 8,
          }}>
            {rupiah(item.harga + extra)}
          </div>
          <button className="tbl utama" disabled={!siap}
                  onClick={() => pilih(terpilih.map((o) => ({
                    modifierId: o.id, nama: o.nama, hargaTambah: o.hargaTambah,
                  })))}>
            Tambahkan
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------- antrean ditolak
function DaftarDitolak({
  daftar, tutup, buangSatu,
}: { daftar: ItemAntrean[]; tutup: () => void; buangSatu: (id: string) => Promise<void> }) {
  return (
    <div className="tirai" onClick={(e) => e.target === e.currentTarget && tutup()}>
      <div className="modal">
        <div className="mh">
          <div>
            <h2>Perlu perhatian</h2>
            <div className="sub">{daftar.length} item ditolak server</div>
          </div>
          <button className="x" onClick={tutup} aria-label="Tutup">×</button>
        </div>
        <div className="mb">
          <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: '0 0 14px', lineHeight: 1.5 }}>
            Item di bawah ditolak karena alasan yang tidak akan berubah kalau dicoba lagi.
            Catat nomornya sebelum dibuang — pelanggannya mungkin sudah membayar.
          </p>
          {daftar.map((d) => (
            <div className="tolak-item" key={d.id}>
              <b>{(d.muatan as any).nomorStruk ?? d.id.slice(0, 8)}</b>
              <p>{d.ditolak?.pesan}</p>
              <button className="tbl samar" style={{ padding: '7px 12px', fontSize: 12 }}
                      onClick={() => void buangSatu(d.id)}>
                Sudah dicatat, buang
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
